import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  MoreVertical,
  Zap
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import type { Automation } from '@/types/automation';

interface AutomationCardCompactProps {
  automation: Automation;
  onEdit: (id: string) => void;
  onToggle: (id: string, isActive: boolean) => void;
  onDelete: (id: string) => void;
  onMove?: (id: string) => void;  // For moving to folders
  draggable?: boolean;
  onDragStart?: (e: React.DragEvent, automationId: string) => void;
  onDragEnd?: (e: React.DragEvent) => void;
  folderName?: string;  // Show folder name when searching
  folderColor?: string; // Folder color for badge
}

export function AutomationCardCompact({
  automation,
  onEdit,
  onToggle,
  onDelete,
  onMove,
  draggable = false,
  onDragStart,
  onDragEnd,
  folderName,
  folderColor
}: AutomationCardCompactProps) {
  const [isDragging, setIsDragging] = useState(false);

  const handleDragStart = (e: React.DragEvent) => {
    setIsDragging(true);
    if (onDragStart && automation.id) {
      onDragStart(e, automation.id);
    }
  };

  const handleDragEnd = (e: React.DragEvent) => {
    setIsDragging(false);
    if (onDragEnd) {
      onDragEnd(e);
    }
  };

  return (
    <div
      data-testid="automation-card"
      data-automation-name={automation.name}
      draggable={draggable}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      className={cn(
        "group h-[52px] border rounded-md px-3 py-1.5 flex items-center justify-between gap-2 transition-all hover:border-primary/50 hover:shadow-sm bg-card",
        isDragging && "opacity-50 cursor-grabbing",
        draggable && !isDragging && "cursor-grab"
      )}
    >
      {/* Left: Icon + Name + Date */}
      <div className="flex items-center gap-2 flex-1 min-w-0">
        {/* Lightning Bolt Icon */}
        <div className="flex-shrink-0">
          <Zap className="w-4 h-4 text-primary" />
        </div>

        {/* Name */}
        <h3
          className="font-medium text-sm truncate cursor-pointer hover:text-primary flex-1 min-w-0"
          onClick={() => automation.id && onEdit(automation.id)}
          title={automation.name}
        >
          {automation.name}
        </h3>

        {/* Folder Badge (when searching) */}
        {folderName && (
          <div
            data-testid="automation-folder-badge"
            data-folder-name={folderName}
            className="flex-shrink-0 px-2 py-0.5 rounded text-xs font-medium"
            style={{
              backgroundColor: folderColor ? `${folderColor}20` : 'hsl(var(--muted))',
              color: folderColor || 'hsl(var(--muted-foreground))'
            }}
            title={`Folder: ${folderName}`}
          >
            {folderName}
          </div>
        )}

        {/* Last Updated Date */}
        {automation.updated_at && (
          <div className="flex-shrink-0 text-xs text-muted-foreground whitespace-nowrap">
            Last Updated: {new Date(automation.updated_at).toLocaleDateString()}
          </div>
        )}
      </div>

      {/* Right: Actions */}
      <div className="flex items-center gap-1 flex-shrink-0">
        {/* Active/Inactive Toggle */}
        <Switch
          data-testid="automation-active-toggle"
          checked={automation.is_active}
          onCheckedChange={() => automation.id && onToggle(automation.id, automation.is_active)}
          className="scale-75"
          title={automation.is_active ? 'Active' : 'Inactive'}
        />

        {/* More Actions Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              aria-label={`Actions for ${automation.name}`}
              className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <MoreVertical className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => automation.id && onEdit(automation.id)}>
              Edit
            </DropdownMenuItem>
            {onMove && (
              <DropdownMenuItem onClick={() => automation.id && onMove(automation.id)}>
                Move to Folder...
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => automation.id && onDelete(automation.id)}
              className="text-destructive focus:text-destructive"
            >
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
